/**
 * @Copyright (c) Microsoft Corporation.  All rights reserved.
 *
 * @file ModuleStore.ts
 */

import * as URL from 'url';
import SPModuleLoaderProvider from './../SPModuleLoaderProvider';

export default class ModuleStore {
  private static _instance: ModuleStore;

  public static get instance(): ModuleStore {
    if (!ModuleStore._instance) {
      ModuleStore._instance = new ModuleStore();
    }

    return ModuleStore._instance;
  }

  private _moduleMap: Map<string, any> = new Map<string, any>(); // tslint:disable-line:no-any

  public getModuleById<TModule>(id: string): TModule {
    return this._moduleMap.get(id);
  }

  public storeModuleById(id: string, module: any): void { // tslint:disable-line:no-any
    if (!this._moduleMap.has(id)) {
      this._moduleMap.set(id, module);
    }
  }

  /**
   * This function contains some magic to load special packages (React, React-DOM, and Flux) while we're serving
   *  from a local workbench without bunding any of them. This function shouldn't be used in any context other
   *  than the local workbench and for specific packages.
   */
  public npmLoadPackage(packageName: string): Promise<any> { // tslint:disable-line:no-any
    if (NPM_BUILD) {
      const url: typeof URL = require('url');
      const relativeReactUrl: string = '__RELATIVE_REACT_PATH__';
      const relativeFluxUrl: string = '__RELATIVE_FLUX_PATH__';

      if (relativeReactUrl === '__RELATIVE_' + 'REACT_PATH__' ||
          relativeFluxUrl  === '__RELATIVE_' + 'FLUX_PATH__') {
        // If the package hasn't been installed (i.e. - it's NPMX linked), don't try to load from incorrect paths
        return undefined;
      }

      const reactUrl: string = url.resolve(__webpack_public_path__, relativeReactUrl);
      const fluxUrl: string = url.resolve(__webpack_public_path__, relativeFluxUrl);

      // Stick these on the ModuleStore object
      /* tslint:disable:no-any */
      const staticContainer: {
        _reactPromise: Promise<any>,
        _fluxPromise: Promise<any>
      } = ModuleStore as any;
      /* tslint:enable:no-any */

      if (!staticContainer._reactPromise) {
       staticContainer._reactPromise = SPModuleLoaderProvider.instance.loadScript(reactUrl);
      }

      if (!staticContainer._fluxPromise) {
        staticContainer._fluxPromise = SPModuleLoaderProvider.instance.loadScript(fluxUrl);
      }

      switch (packageName) {
        case 'react':
          return staticContainer._reactPromise;

        case 'react-dom':
          /* tslint:disable-next-line:no-any */
          return staticContainer._reactPromise.then((react: any) => react.__SECRET_DOM_DO_NOT_USE_OR_YOU_WILL_BE_FIRED);

        case 'flux':
          return staticContainer._fluxPromise;

        default:
          break;
      }
    }

    return undefined;
  }
}



// WEBPACK FOOTER //
// /src/stores/ModuleStore.ts